/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useEffect } from 'react';
import { Modal, Table, Pagination } from 'antd';
import { getTransactionsByFile } from '../../store/slices/screenmaker/screenmaker';

interface TransactionModalProps {
    visible: boolean;
    fileId: number | null;
    onClose: () => void;
}

const TransactionModal: React.FC<TransactionModalProps> = ({ visible, fileId, onClose }) => {
    const [transactions, setTransactions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(9);
    const [total, setTotal] = useState(0);

    useEffect(() => {
        if (visible && fileId) {
            setCurrentPage(1);
            fetchTransactions(0, pageSize);
            fetchTotalCount();
        }
    }, [visible, fileId]);

    const fetchTotalCount = async () => {
        if (!fileId) return;
        try {
            const token = localStorage.getItem("loginToken");
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/campaign/get-trx-by-file/${fileId}/0/100000`,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );
            if (response.ok) {
                const data = await response.json();
                setTotal(data.length || 0);
            }
        } catch (error) {
            console.error(error);
        }
    };

    const fetchTransactions = async (page: number, size: number) => {
        if (!fileId) return;
        
        setLoading(true);
        try {
            const token = localStorage.getItem("loginToken");
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/campaign/get-trx-by-file/${fileId}/${page}/${size}`,
                {
                    headers: {
                        "Authorization": `Bearer ${token}`,
                        "Content-Type": "application/json"
                    }
                }
            );
            
            if (response.ok) {
                const data = await response.json();
                setTransactions(data || []);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Transaction ID',
            dataIndex: 'transactionId',
            key: 'transactionId',
            width: 130,
            render: (val: any) => val ?? '-',
        },
        {
            title: 'Bank Transaction ID',
            dataIndex: 'bankTransactionId',
            key: 'bankTransactionId',
            width: 160,
            render: (val: any) => val ?? '-',
        },
        {
            title: 'Bank Customer ID',
            dataIndex: 'customerIdentifier',
            key: 'customerIdentifier',
            width: 180,
            render: (val: any) => val ?? '-',
        },
        {
            title: 'Customer Name',
            dataIndex: 'accountShortName',
            key: 'accountShortName',
            width: 150,
            render: (val: any) => val ?? '-',
        },
        {
            title: 'Amount',
            dataIndex: 'transactionAmount',
            key: 'transactionAmount',
            width: 130,
            render: (amount: number) => amount != null ? `OMR ${Number(amount).toFixed(2)}` : '-',
        },
        {
            title: 'Transaction Date',
            dataIndex: 'transactionDate',
            key: 'transactionDate',
            width: 140,
            render: (date: string) => date ? new Date(date).toLocaleDateString("en-GB") : '-',
        },
        {
            title: 'Merchant Category Code',
            dataIndex: 'merchantCategoryCode',
            key: 'merchantCategoryCode',
            width: 180,
            render: (val: any) => val ?? '-',
        },
        {
            title: 'Card Number',
            dataIndex: 'cardNumber',
            key: 'cardNumber',
            width: 160,
            render: (val: any) => val ?? '-',
        },
    ];

    return (
        <Modal
            title="File Transactions"
            open={visible}
            onCancel={onClose}
            width={1100}
            footer={null}
        >
            <Table
                loading={loading}
                columns={columns}
                dataSource={transactions}
                pagination={false}
                rowKey="transactionId"
                size="small"
                scroll={{ x: 1180 }}
            />
            <Pagination
                style={{ marginTop: 16, textAlign: 'right' }}
                current={currentPage}
                total={total}
                pageSize={pageSize}
                pageSizeOptions={[9, 18, 36, 72]}
                showSizeChanger
                showTotal={(total) => `Total ${total} items`}
                onChange={(page, size) => {
                    if (size !== pageSize) {
                        setCurrentPage(1);
                        setPageSize(size);
                        fetchTransactions(0, size);
                    } else {
                        setCurrentPage(page);
                        fetchTransactions(page - 1, size);
                    }
                }}
            />
        </Modal>
    );
};

export default TransactionModal;
